import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, AmenityMentionEntry } from '../../services/analyticsService';

interface AmenitySentimentChartProps {
  businessIdentifier?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const AmenitySentimentChart: React.FC<AmenitySentimentChartProps> = ({
  businessIdentifier,
}) => {
  const [data, setData] = useState<AmenityMentionEntry[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!businessIdentifier) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await analyticsService.getAmenityMentions(businessIdentifier);
        setData(result.data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch amenity sentiment data';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [businessIdentifier]);

  const getChartOption = () => {
    if (!data || data.length === 0) return {};

    // Top 10 amenities by total count
    const sorted = [...data].sort((a, b) => b.count - a.count).slice(0, 10);
    // Reverse for horizontal bar (highest at top)
    const reversed = [...sorted].reverse();
    const amenities = reversed.map((d) => d.amenity);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          const item = reversed[idx];
          if (!item) return '';
          return `<b>${item.amenity}</b><br/>Positive: ${item.positive}<br/>Neutral: ${item.neutral}<br/>Negative: ${item.negative}<br/>Total: ${item.count}`;
        },
      },
      legend: {
        data: ['Positive', 'Neutral', 'Negative'],
        bottom: 0,
        textStyle: { fontFamily: FONT_FAMILY, fontSize: 11 },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '12%',
        top: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      yAxis: {
        type: 'category',
        data: amenities,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#666',
          width: 130,
          overflow: 'truncate',
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
      },
      series: [
        {
          name: 'Positive',
          type: 'bar',
          stack: 'sentiment',
          data: reversed.map((d) => d.positive),
          itemStyle: { color: '#48BB78', borderRadius: [0, 0, 0, 0] },
          barWidth: '50%',
        },
        {
          name: 'Neutral',
          type: 'bar',
          stack: 'sentiment',
          data: reversed.map((d) => d.neutral),
          itemStyle: { color: '#A0AEC0' },
          barWidth: '50%',
        },
        {
          name: 'Negative',
          type: 'bar',
          stack: 'sentiment',
          data: reversed.map((d) => d.negative),
          itemStyle: { color: '#F56565', borderRadius: [0, 4, 4, 0] },
          barWidth: '50%',
        },
      ],
    };
  };

  const hasData = data && data.length > 0;

  return (
    <ChartCard title="Amenity Mentions vs Sentiment" isLoading={isLoading} error={error}>
      {hasData ? (
        <ReactECharts
          option={getChartOption()}
          style={{ height: Math.max(300, Math.min(data!.length, 10) * 36) + 'px', width: '100%' }}
          opts={{ renderer: 'svg' }}
        />
      ) : (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>
            Coming Soon — Data will appear as new conversations are processed
          </Text>
        </Flex>
      )}
    </ChartCard>
  );
};

export default AmenitySentimentChart;
